import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Menú contextual compartido: portal al <body>, fijo a la ventana, con flip.
 *
 * Por qué existe (la misma historia, tres veces):
 * - `absolute` dentro de un contenedor con overflow se recorta — y un `overflow-x: auto`
 *   recorta TAMBIÉN en vertical (cuando un eje deja de ser `visible`, el otro pasa de
 *   `visible` a `auto`). Caso CRUO 24-08 en Preinscripciones.
 * - `fixed` escapa del recorte, pero el scroll no lo arrastra, y «hacia abajo siempre»
 *   lo saca del viewport cuando el botón está en la mitad baja de la pantalla: las
 *   opciones quedan inalcanzables (caso Kong 02-09 con el menú «Más» de Socios).
 *
 * Reglas, heredadas de la implementación de PreInscripcionesPage:
 * - Debajo del botón si cabe, arriba si no. La altura se MIDE (`offsetHeight`), nunca
 *   se hardcodea: las constantes envejecen con cada cambio de estilos (el `ALTO = 152`
 *   del ⋮ de Socios quedó desfasado en cuanto #653 cambió la altura del botón).
 * - Alineado a la derecha del botón, sin salirse por ningún lado.
 * - Fijo a la ventana: si la página scrollea o cambia de tamaño se cierra, en vez de
 *   quedarse colgado mintiendo sobre a qué botón pertenece. También cierran Escape y
 *   el mousedown fuera.
 *
 * Uso:
 *   const [anchorEl, setAnchorEl] = useState(null);
 *   <button onClick={(e) => setAnchorEl((prev) => (prev ? null : e.currentTarget))}>Más</button>
 *   <MenuFlotante anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
 *     <button type="button" role="menuitem" onClick={...}>Opción</button>
 *   </MenuFlotante>
 */
export default function MenuFlotante({ anchorEl, onClose, className = '', children }) {
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const onDown = (e) => {
      // El botón que abre queda fuera del cierre: su propio onClick hace de toggle.
      if (anchorEl.contains(e.target) || menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScrollOrResize = () => onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, anchorEl, onClose]);

  // Colocación: se pinta invisible, se mide de verdad, y entonces se decide arriba/abajo.
  useEffect(() => {
    if (!open) return;
    const b = anchorEl.getBoundingClientRect();
    const alto = menuRef.current?.offsetHeight || 96;
    const margen = 8;
    const cabeDebajo = b.bottom + 4 + alto + margen <= window.innerHeight;
    const top = cabeDebajo ? b.bottom + 4 : Math.max(margen, b.top - 4 - alto);
    const ancho = menuRef.current?.offsetWidth || 180;
    const left = Math.min(
      Math.max(margen, b.right - ancho),
      Math.max(margen, window.innerWidth - ancho - margen)
    );
    setPos({ top, left });
  }, [open, anchorEl]);

  if (!open || typeof document === 'undefined' || !document.body) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      role="menu"
      // z-[210]: por encima de cabeceras sticky (z-30). Los modales van por CustomModal.
      className={`fixed z-[210] rounded-lg border border-gray-200 bg-white py-1 shadow-lg ${className}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}
